import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { GeneralService } from 'src/app/services/general/general.service';
import { VehicleService } from 'src/app/services/vehicle/vehicle.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-vh-manager-upload-images',
  templateUrl: './vh-manager-upload-images.component.html',
  styleUrls: ['./vh-manager-upload-images.component.scss']
})
export class VhManagerUploadImagesComponent extends DestructorComponent implements OnInit {

  @ViewChild('multiImageInput') multiImageInput!: ElementRef<HTMLInputElement>;
  @Output() imagesUploaded = new EventEmitter<void>();

  selectedVehicle: any = null;
  selectedImages: File[] = [];
  uploadingImages: boolean = false;
  uploadProgress: { current: number; total: number } = { current: 0, total: 0 };
  uploadedImageUrls: string[] = [];

  constructor(
    private _generalService: GeneralService,
    private _vehicleService: VehicleService
  ) {
    super();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_vh_manager_upload_images');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const vehicle = (window as any).selectedVehicleForImageUpload;
        if (vehicle) {
          this.selectedVehicle = vehicle;
          this.resetUploadState();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this.resetUploadState();
        this.selectedVehicle = null;
      });
    }
  }

  private resetUploadState(): void {
    this.selectedImages = [];
    this.uploadingImages = false;
    this.uploadProgress = { current: 0, total: 0 };
    this.uploadedImageUrls = [];

    // Reset file input
    if (this.multiImageInput) {
      this.multiImageInput.nativeElement.value = '';
    }
  }

  onMultiImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedImages = Array.from(input.files);
    }
  }

  triggerMultiImageUpload(): void {
    this.multiImageInput.nativeElement.click();
  }

  removeImage(index: number): void {
    if (this.selectedVehicle && this.selectedVehicle.pictures) {
      this.selectedVehicle.pictures.splice(index, 1);
    }
  }

  hasVehicleImages(): boolean {
    return this.selectedVehicle && this.selectedVehicle.pictures && this.selectedVehicle.pictures.length > 0;
  }

  getFilePreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  async uploadImages(): Promise<void> {
    if (this.selectedImages.length === 0) {
      this.showError('Please select images to upload.');
      return;
    }

    this.uploadingImages = true;
    this.uploadProgress = { current: 0, total: this.selectedImages.length };
    this.uploadedImageUrls = [];

    try {
      for (let i = 0; i < this.selectedImages.length; i++) {
        const file = this.selectedImages[i];
        this.uploadProgress.current = i + 1;

        const imageUrl = await this.uploadSingleImage(file);
        this.uploadedImageUrls.push(imageUrl);

        // Add to vehicle pictures array
        if (!this.selectedVehicle.pictures) {
          this.selectedVehicle.pictures = [];
        }
        this.selectedVehicle.pictures.push(imageUrl);
      }

      // Update vehicle via API
      await this.updateVehicleImages();

      this.showSuccess(`Successfully uploaded ${this.selectedImages.length} image(s)!`);
      this.resetUploadState();
      this.closeModal();
      this.imagesUploaded.emit();

    } catch (error) {
      console.error('Error uploading images:', error);
      this.showError('Error uploading some images. Please try again.');
    } finally {
      this.uploadingImages = false;
      this.uploadProgress = { current: 0, total: 0 };
    }
  }

  private uploadSingleImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      this._generalService.uploadFileByContent({ "contentType": file.type }).subscribe({
        next: (res) => {
          const imageUrl = res.data.url;
          const formData = this._generalService.convertKeysToFormdata(res.data.result.fields, file);

          this._generalService.sendFileToS3(res.data.result.url, formData).subscribe({
            next: () => {
              resolve(imageUrl);
            },
            error: (error) => {
              reject(error);
            }
          });
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  }

  private updateVehicleImages(): Promise<void> {
    return new Promise((resolve, reject) => {
      const updateData = {
        pictures: this.selectedVehicle.pictures || []
      };

      this.subscriptions.push(
        this._vehicleService.UpdateItems(this.selectedVehicle._id, updateData).subscribe({
          next: (response: any) => {
            if (response.status) {
              resolve();
            } else {
              reject(new Error(response.message || 'Failed to update vehicle images'));
            }
          },
          error: (error: any) => {
            reject(error);
          }
        })
      );
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_vh_manager_upload_images');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private showSuccess(message: string): void {
    Swal.fire({
      text: message,
      icon: "success",
      buttonsStyling: false,
      confirmButtonText: "Ok, got it!",
      customClass: {
        confirmButton: "btn btn-primary"
      }
    });
  }

  private showError(message: string): void {
    Swal.fire({
      text: message,
      icon: "error",
      buttonsStyling: false,
      confirmButtonText: "Ok, got it!",
      customClass: {
        confirmButton: "btn btn-primary"
      }
    });
  }
}
